import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  Calendar,
  ChevronDown,
  FileText,
  Globe,
  Heart,
  Home,
  QrCode,
  Settings,
  Shield,
  TrendingUp,
  User,
  Users,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotificationRefresh } from '../../contexts/NotificationContext'
import { ArtemisWordmark } from '../brand'

const MAIN_NAV = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Acceso QR', href: '/acceso-mascotas', icon: QrCode },
  { name: 'Usuarios', href: '/usuarios', icon: Users },
  { name: 'Veterinarios', href: '/veterinarios', icon: User },
  { name: 'Clínicas', href: '/clinicas', icon: Building2 },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell, useUnreadCount: true },
]

const CONFIG_NAV = [
  { name: 'Roles y Usuarios', href: '/configuracion/roles', icon: Shield },
  { name: 'Condiciones de Salud', href: '/configuracion/condiciones-salud', icon: Activity },
  { name: 'Push condición salud', href: '/configuracion/push-condicion-salud', icon: Bell },
  { name: 'SEO', href: '/configuracion/seo', icon: TrendingUp },
  { name: 'Países', href: '/configuracion/paises', icon: Globe },
  { name: 'Sistema', href: '/configuracion/sistema', icon: Settings },
]

function getInitials(user) {
  const source = `${user?.nombre || ''} ${user?.apellido || ''}`.trim()
  if (!source) return 'SA'
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('')
}

export default function AdminAppSidebar({ onNavigate }) {
  const location = useLocation()
  const { user } = useAuth()
  const { contadorNoLeidas } = useNotificationRefresh()

  const [configOpen, setConfigOpen] = useState(location.pathname.startsWith('/configuracion'))

  const isConfigActive = useMemo(
    () => CONFIG_NAV.some((item) => location.pathname === item.href),
    [location.pathname]
  )

  const unreadCount = Number(contadorNoLeidas) > 0 ? Number(contadorNoLeidas) : 0

  return (
    <aside className="w-[230px] bg-[#151e2d] border-r border-[#243246] flex flex-col h-full">
      <div className="px-4 py-4 border-b border-[#243246]">
        <ArtemisWordmark mascotSize={32} className="[&_div]:text-[#e8edf5]" />
      </div>

      <nav className="flex-1 px-2.5 py-2.5 overflow-y-auto">
        {MAIN_NAV.map((item) => {
          const active = location.pathname === item.href
          const Icon = item.icon
          const badgeCount = item.useUnreadCount ? unreadCount : 0

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              className={[
                'flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[13px] transition-colors',
                active
                  ? 'bg-[rgba(0,200,150,0.12)] text-[#00c896]'
                  : 'text-[#7a8fa6] hover:bg-[rgba(0,200,150,0.06)] hover:text-[#e8edf5]',
              ].join(' ')}
            >
              <Icon className="w-[17px] h-[17px] shrink-0" />
              <span className="truncate">{item.name}</span>
              {badgeCount > 0 ? (
                <span className="ml-auto bg-[#ef5350] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              ) : null}
            </Link>
          )
        })}

        <div className="text-[10px] uppercase tracking-[0.08em] text-[#3d5166] font-medium px-3 pt-4 pb-1">
          Configuración
        </div>

        <button
          type="button"
          onClick={() => setConfigOpen((prev) => !prev)}
          className={[
            'w-full flex items-center justify-between px-3 py-2.5 rounded-[10px] text-[13px] transition-colors',
            isConfigActive
              ? 'bg-[rgba(0,200,150,0.12)] text-[#00c896]'
              : 'text-[#7a8fa6] hover:bg-[rgba(0,200,150,0.06)] hover:text-[#e8edf5]',
          ].join(' ')}
        >
          <span className="inline-flex items-center gap-2.5">
            <Settings className="w-[17px] h-[17px] shrink-0" />
            Configuración
          </span>
          <ChevronDown className={['w-4 h-4 transition-transform', configOpen ? 'rotate-180' : 'rotate-0'].join(' ')} />
        </button>

        {configOpen ? (
          <div className="pl-3 mt-1 space-y-1">
            {CONFIG_NAV.map((item) => {
              const active = location.pathname === item.href
              const Icon = item.icon
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={onNavigate}
                  className={[
                    'flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-xs transition-colors',
                    active
                      ? 'bg-[rgba(0,200,150,0.12)] text-[#00c896]'
                      : 'text-[#7a8fa6] hover:bg-[rgba(0,200,150,0.06)] hover:text-[#e8edf5]',
                  ].join(' ')}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{item.name}</span>
                </Link>
              )
            })}
          </div>
        ) : null}
      </nav>

      <div className="p-3.5 border-t border-[#243246]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#00c896] text-[#0f1623] text-xs font-semibold flex items-center justify-center">
            {getInitials(user)}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-[#e8edf5] truncate">
              {[user?.nombre, user?.apellido].filter(Boolean).join(' ') || 'Super Admin'}
            </p>
            <p className="text-[10px] text-[#7a8fa6]">Administrador</p>
          </div>
        </div>
      </div>
    </aside>
  )
}
